import React from "react";
import { useParams } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Snowflake, Flame, Thermometer, Boxes, FunctionSquare, AlertTriangle } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading, StatusBadge, Kpi, DataRow } from "@/components/common";
import { inr, inrL, num, statusOf } from "@/lib/format";

const TWIN_META = {
  refrigeration: { icon: Snowflake, trendKey: "cop", trendLabel: "COP", expectedFrom: "expected_cop",
    kpis: [["cop", "COP", ""], ["tr", "Cooling", "TR"], ["power_kw", "Power", "kW"], ["inr_per_l", "Cost", "₹/L"]] },
  boiler: { icon: Flame, trendKey: "boiler_eff", trendLabel: "Efficiency %", expectedFrom: "expected_efficiency_pct",
    kpis: [["efficiency_pct", "Efficiency", "%"], ["steam_flow_kg_hr", "Steam", "kg/hr"], ["steam_cost_per_kg", "Steam Cost", "₹/kg"], ["inr_per_l", "Cost", "₹/L"]] },
  pasteurization: { icon: Thermometer, trendKey: null, trendLabel: "Regen %", expectedFrom: "expected_regen_pct",
    kpis: [["regen_eff_pct", "Regeneration", "%"], ["flow_lph", "Flow", "LPH"], ["holding_s", "Holding", "s"], ["inr_per_l", "Cost", "₹/L"]] },
};

const LABELS = {
  cop: "COP", expected_cop: "Expected COP", tr: "Cooling (TR)", power_kw: "Power (kW)", kwh_per_tr: "kWh/TR",
  cost_per_tr: "₹/TR", inr_per_l: "Cost ₹/L", rei: "REI", efficiency_pct: "Efficiency %", expected_efficiency_pct: "Expected Eff %",
  steam_flow_kg_hr: "Steam kg/hr", fuel_flow_kg_hr: "Fuel kg/hr", steam_cost_per_kg: "Steam ₹/kg", stack_temp_c: "Stack °C",
  steam_per_l: "Steam/L", regen_eff_pct: "Regeneration %", expected_regen_pct: "Expected Regen %", flow_lph: "Flow LPH",
  past_temp_c: "Past °C", holding_s: "Holding (s)", cycles_day: "Cycles/day", utility_per_l: "Utility ₹/L", total_per_hr: "₹/hr",
};

export default function TwinDetail() {
  const { id } = useParams();
  const { data } = usePoll(() => endpoints.twin(id), 5000, [id]);
  if (!data || !data.twin) return <Loading />;
  const t = data.twin;
  const meta = TWIN_META[id] || { icon: Boxes, kpis: Object.keys(t.metrics || {}).slice(0, 4).map((k) => [k, LABELS[k] || k, ""]) };
  const Icon = meta.icon;
  const s = statusOf(t.health);

  const trend = (data.trend || []).map((d) => ({ ...d, t: new Date(d.t).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit" }) }));

  return (
    <div data-testid={`twin-${id}`}>
      <PageHeader icon={Icon} title={t.name}
        subtitle={`${t.category} digital twin · physics-based virtual sensors & economics`}
        right={<StatusBadge status={t.health} testid="twin-health" />} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {meta.kpis.map(([key, label, unit]) => (
          <Kpi key={key} testid={`twin-kpi-${key}`} label={label} value={typeof t.metrics[key] === "number" ? num(t.metrics[key], key.includes("inr") || key.includes("cost") || key === "cop" ? 2 : (t.metrics[key] < 100 ? 1 : 0)) : t.metrics[key]} unit={unit}
            accent={s.color} />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Panel testid="panel-virtual-sensors" title="Virtual Sensors" subtitle="Expected (physics) vs Actual">
          {t.virtual_sensors ? (
            <div className="space-y-3">
              {Object.entries(t.virtual_sensors).map(([k, v]) => (
                <div key={k}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500">{LABELS[k] || k.replace(/_/g, " ")}</span>
                    <span className="font-mono font-semibold text-slate-700">{num(v, v < 100 ? 2 : 0)}</span>
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-400">
                Anomaly detection compares actual measurements against these expected values.
              </div>
            </div>
          ) : <div className="text-sm text-slate-400">No virtual sensors.</div>}
        </Panel>

        <Panel testid="panel-equipment" title="Equipment Mimic" subtitle="Assets in this twin" className="xl:col-span-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {(t.assets || []).map((a) => (
              <div key={a} className="border-2 rounded-md p-3 text-center transition-colors" style={{ borderColor: s.color }}>
                <Boxes size={18} className="mx-auto mb-1.5 text-slate-400" />
                <div className="text-[11px] font-medium text-slate-700 leading-tight">{a}</div>
                <span className="inline-block mt-1.5 w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {meta.trendKey && trend.length > 0 && (
        <Panel testid="panel-twin-trend" title={`${meta.trendLabel} Trend`} subtitle="Live performance (last ~30 min)" className="mb-4">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
              <Tooltip />
              <Line type="linear" dataKey={meta.trendKey} name={meta.trendLabel} stroke={s.color} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel testid="panel-twin-anomalies" title="AI Insights & Anomalies" subtitle="Actual vs expected with ₹ impact">
          {data.anomalies.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">Operating near expected — no anomalies detected. ✓</div>
          ) : (
            <div className="space-y-3">
              {data.anomalies.map((a, i) => (
                <div key={i} className={`border-l-2 rounded-r p-3 ${statusOf(a.severity).bg}`} style={{ borderColor: statusOf(a.severity).color }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                      <AlertTriangle size={14} style={{ color: statusOf(a.severity).color }} /> {a.label}
                    </span>
                    <StatusBadge status={a.severity} />
                  </div>
                  <div className="text-xs text-slate-600 mb-1.5">
                    {a.metric}: actual <b>{a.actual}</b> vs expected <b>{a.expected}</b> ({a.deviation_pct}% deviation, {Math.round(a.confidence * 100)}% confidence)
                  </div>
                  <div className="text-xs font-mono font-semibold text-red-600 mb-1.5">{inr(a.impact_per_day)}/day · {inr(a.impact_per_year)}/yr</div>
                  {a.root_causes?.length > 0 && (
                    <div className="text-[11px] text-slate-500">Likely causes: {a.root_causes.join(" · ")}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel testid="panel-calc-trace" title="Calculation Objects" subtitle="Explainability — every output traceable to its equation">
          <div className="space-y-2.5">
            {(t.calc_trace || []).map((c, i) => (
              <div key={i} className="border border-slate-100 rounded p-2.5">
                <div className="flex items-center gap-2 mb-1">
                  <FunctionSquare size={14} className="text-blue-500" />
                  <span className="text-[11px] font-bold text-slate-700">{c.object_id} · {c.name}</span>
                </div>
                <code className="text-[12px] text-blue-700 font-mono block mb-1">{c.equation}</code>
                <div className="text-[10px] text-slate-400 font-mono">
                  in: {Object.entries(c.inputs).map(([k, v]) => `${k}=${v}`).join(", ")}
                </div>
                <div className="text-[10px] text-emerald-600 font-mono">
                  out: {Object.entries(c.outputs).map(([k, v]) => `${k}=${v}`).join(", ")}
                </div>
              </div>
            ))}
            {(!t.calc_trace || t.calc_trace.length === 0) && <div className="text-sm text-slate-400">No calculation trace.</div>}
          </div>
        </Panel>
      </div>
    </div>
  );
}
